// src/utils/exportAssessmentPDF.js
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { getQuestionsForSector } from "./questions";
import ecoTrackLogo from "../assets/ecotrack-logo.png";

/*
  EcoTrack PDF export
  Layout method: HTML template -> html2canvas -> jsPDF
  This is designed to visually match the HTML report preview.
*/

/* ---------- SECTOR BENCHMARKS ---------- */
const benchmarkData = {
  Manufacturing: { Environmental: 62, Social: 55, Governance: 60 },
  Finance: { Environmental: 48, Social: 61, Governance: 72 },
  "Agriculture/Food": { Environmental: 58, Social: 57, Governance: 50 },
  Tech: { Environmental: 54, Social: 68, Governance: 65 },
  Construction: { Environmental: 60, Social: 56, Governance: 52 },
  Transportation: { Environmental: 64, Social: 58, Governance: 51 },
  "Textile/Fashion": { Environmental: 59, Social: 60, Governance: 49 },
  Furniture: { Environmental: 61, Social: 59, Governance: 47 },
};

/* ---------- BASIC HELPERS ---------- */
function safeText(value, fallback = "—") {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value);
}

function escapeHtml(value) {
  return safeText(value, "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function sanitizeFilename(value) {
  return String(value || "assessment")
    .replace(/[^a-z0-9]/gi, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function clampPercent(value) {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  const normalized = value <= 1 ? value * 100 : value;
  return Math.max(0, Math.min(100, Math.round(normalized)));
}

function formatDate(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;

    if (!d || Number.isNaN(d.getTime())) return "—";

    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function getScoreFields(assessment = {}) {
  return {
    overall:
      clampPercent(assessment.overallScore) ??
      clampPercent(assessment.overall) ??
      clampPercent(assessment.score) ??
      0,

    env:
      clampPercent(assessment.envScore) ??
      clampPercent(assessment.pillarScores?.E) ??
      clampPercent(assessment.pillars?.E) ??
      0,

    soc:
      clampPercent(assessment.socScore) ??
      clampPercent(assessment.pillarScores?.S) ??
      clampPercent(assessment.pillars?.S) ??
      0,

    gov:
      clampPercent(assessment.govScore) ??
      clampPercent(assessment.pillarScores?.G) ??
      clampPercent(assessment.pillars?.G) ??
      0,
  };
}

function getMaturityLabel(score) {
  const s = Number(score || 0);
  if (s <= 20) return "Critical";
  if (s <= 40) return "High Risk";
  if (s <= 60) return "Developing";
  if (s <= 80) return "Advanced";
  return "Leader";
}


function pillarLabel(pillar) {
  const p = String(pillar || "").toUpperCase();
  if (p === "E") return "Environmental";
  if (p === "S") return "Social";
  if (p === "G") return "Governance";
  return safeText(pillar);
}

function pillarCode(pillar) {
  const p = String(pillar || "").toLowerCase();

  if (p === "environmental" || p === "e") return "E";
  if (p === "social" || p === "s") return "S";
  if (p === "governance" || p === "g") return "G";

  return safeText(pillar);
}

function normalizeAnswer(value) {
  if (value === undefined || value === null || value === "") {
    return {
      label: "Not answered",
      maturity: "Not answered",
      score: "—",
      scoreValue: null,
      pillClass: "amber",
    };
  }

  if (typeof value === "number") {
    const v = Math.max(0, Math.min(4, value));

    const map = {
      0: {
        label: "Not in place",
        maturity: "Not in place",
        pillClass: "red",
      },
      1: {
        label: "Informal / ad hoc",
        maturity: "Informal / Ad hoc",
        pillClass: "amber",
      },
      2: {
        label: "Partially structured",
        maturity: "Partially structured",
        pillClass: "sky",
      },
      3: {
        label: "Implemented & documented",
        maturity: "Implemented & documented",
        pillClass: "mint",
      },
      4: {
        label: "Advanced / best practice",
        maturity: "Advanced / best practice",
        pillClass: "mint",
      },
    };

    return {
      ...map[v],
      score: `${v}/4`,
      scoreValue: v,
    };
  }

  if (typeof value === "object") {
    const rawScore =
      typeof value.score === "number"
        ? value.score
        : typeof value.answerScore === "number"
        ? value.answerScore
        : typeof value.numericScore === "number"
        ? value.numericScore
        : typeof value.value === "number"
        ? value.value
        : typeof value.points === "number"
        ? value.points
        : null;

    if (typeof rawScore === "number") {
      return normalizeAnswer(rawScore);
    }

    const label =
      value.label ||
      value.answerLabel ||
      value.answer ||
      value.text ||
      "—";

    return {
      label,
      maturity: label,
      score: "—",
      scoreValue: null,
      pillClass: "amber",
    };
  }

  const str = String(value);
  const lower = str.trim().toLowerCase();

  if (lower === "yes") {
    return {
      label: "Yes",
      maturity: "Implemented & documented",
      score: "4/4",
      scoreValue: 4,
      pillClass: "mint",
    };
  }

  if (lower === "no") {
    return {
      label: "No",
      maturity: "Not in place",
      score: "0/4",
      scoreValue: 0,
      pillClass: "red",
    };
  }

  if (lower === "partial") {
    return {
      label: "Partial",
      maturity: "Partially structured",
      score: "2/4",
      scoreValue: 2,
      pillClass: "sky",
    };
  }

  if (lower === "unknown") {
    return {
      label: "Unknown",
      maturity: "Not answered",
      score: "—",
      scoreValue: null,
      pillClass: "amber",
    };
  }

  return {
    label: str,
    maturity: str,
    score: "—",
    scoreValue: null,
    pillClass: "amber",
  };
}

function getBenchmark(sector, assessment = {}) {
  if (assessment.benchmark) {
    return {
      Environmental: assessment.benchmark.sectorAvgE ?? null,
      Social: assessment.benchmark.sectorAvgS ?? null,
      Governance: assessment.benchmark.sectorAvgG ?? null,
    };
  }

  return benchmarkData[sector] || null;
}

function getOverallBenchmark(scores, benchmark) {
  if (!benchmark) return null;

  const values = [
    benchmark.Environmental,
    benchmark.Social,
    benchmark.Governance,
  ].filter((v) => typeof v === "number");

  if (!values.length) return null;

  return Math.round(values.reduce((sum, v) => sum + v, 0) / values.length);
}
function benchmarkSentence(pillar, score, benchmarkValue) {
  if (typeof score !== "number" || typeof benchmarkValue !== "number") {
    return `${pillar} benchmark data is not available for this assessment.`;
  }

  const delta = score - benchmarkValue;

  if (delta >= 10) {
    return `${pillar} performance is significantly above the sector benchmark (+${delta} pts).`;
  }

  if (delta >= 0) {
    return `${pillar} performance is aligned with the sector benchmark (${delta >= 0 ? "+" : ""}${delta} pts).`;
  }

  if (delta >= -10) {
    return `${pillar} performance is slightly below the sector benchmark (${delta} pts).`;
  }

  return `${pillar} performance is significantly below the sector benchmark (${delta} pts).`;
}

function getWeakestPillar(scores) {
  const rows = [
    ["Environmental", scores.env],
    ["Social", scores.soc],
    ["Governance", scores.gov],
  ];

  rows.sort((a, b) => a[1] - b[1]);
  return rows[0];
}
function buildKeyFindingsFromRows(rows = [], scores = {}) {
  const findings = [];

  const weak = getWeakestPillar(scores);

  findings.push({
    type: "warn",
    text: `${weak[0]} is currently the weakest ESG pillar.`,
  });

  const lowRows = rows
    .filter((row) => {
      const scoreValue = Number(String(row.score || "").split("/")[0]);
      return !Number.isNaN(scoreValue) && scoreValue <= 1;
    })
    .slice(0, 4);

  lowRows.forEach((row) => {
    findings.push({
      type: "warn",
      text: row.question,
    });
  });

  const strongRows = rows
    .filter((row) => {
      const scoreValue = Number(String(row.score || "").split("/")[0]);
      return !Number.isNaN(scoreValue) && scoreValue >= 3;
    })
    .slice(0, 2);

  strongRows.forEach((row) => {
    findings.push({
      type: "ok",
      text: row.question,
    });
  });

  return findings.slice(0, 5);
}

function getRiskLevel(scores) {
  const values = [scores.env, scores.soc, scores.gov].filter(
    (v) => typeof v === "number"
  );

  if (values.some((v) => v <= 20)) return "Critical Risk";
  if ((scores.overall || 0) <= 40) return "High Risk";
  if ((scores.overall || 0) <= 60) return "Moderate Risk";
  if ((scores.overall || 0) <= 80) return "Controlled Risk";
  return "Low Risk";
}

function getRiskText(scores) {
  const risk = getRiskLevel(scores);

  if (risk === "Critical Risk") {
    return "One or more ESG pillars show a critical weakness. Immediate corrective action should be prioritised before interpreting the overall score as balanced performance.";
  }

  if (risk === "High Risk") {
    return "Current ESG practices appear fragmented or weak. The organisation should focus on basic policies, responsibilities, data collection and internal controls.";
  }

  if (risk === "Moderate Risk") {
    return "Current ESG practices reduce immediate exposure but weaknesses may increase regulatory, operational or reputational risks over time.";
  }

  if (risk === "Controlled Risk") {
    return "The organisation shows a structured ESG approach, although further improvement is still needed to strengthen evidence, monitoring and accountability.";
  }

  return "The organisation shows strong ESG maturity. The focus should be continuous improvement, monitoring and transparent communication.";
}

function prepareSuggestions(assessment = {}) {
  const raw =
    assessment.suggestionPreview ||
    assessment.tailoredSuggestions ||
    assessment.suggestions ||
    assessment.recommendations ||
    [];

  if (!Array.isArray(raw)) return [];

  return raw
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          id: `suggestion-${index}`,
          title: `Recommended action ${index + 1}`,
          text: item,
          pillar: "",
          impact: index < 3 ? "High" : "Medium",
          effort: "Medium",
          timeframe: index < 3 ? "0–6 months" : "6–12 months",
        };
      }

      return {
        id: item.id || `suggestion-${index}`,
        title: item.title || `Recommended action ${index + 1}`,
        text: item.text || item.action || item.description || "",
        pillar: item.pillar || "",
        impact: item.impact || (index < 3 ? "High" : "Medium"),
        effort: item.effort || "Medium",
        timeframe: item.timeframe || (index < 3 ? "0–6 months" : "6–12 months"),
      };
    })
    .filter((item) => item.text);
}

function dots(level) {
  const value = String(level || "").toLowerCase();

  let active = 3;

  if (value.includes("high")) active = 4;
  if (value.includes("low")) active = 2;
  if (value.includes("medium")) active = 3;

  return Array.from({ length: 5 })
    .map((_, i) => `<span class="${i < active ? "on" : ""}"></span>`)
    .join("");
}
function buildRoadmapItems(suggestions = []) {
  const fallback = [
    {
      title: "Define ESG Responsibilities",
      text: "Assign ESG ownership to a designated individual or team.",
      timeframe: "0–6 Months",
      impact: "High",
      effort: "Medium",
    },
    {
      title: "Establish Supplier Sustainability Screening",
      text: "Introduce ESG criteria into supplier selection and monitoring.",
      timeframe: "0–6 Months",
      impact: "High",
      effort: "Medium",
    },
    {
      title: "Create Basic ESG KPI Dashboard",
      text: "Track key environmental, social and governance indicators.",
      timeframe: "0–6 Months",
      impact: "High",
      effort: "Medium",
    },
    {
      title: "Develop ESG Policy",
      text: "Formalize your commitment and governance framework.",
      timeframe: "6–12 Months",
      impact: "Medium",
      effort: "Medium",
    },
    {
      title: "Implement Employee Engagement Metrics",
      text: "Measure and improve employee satisfaction and well-being.",
      timeframe: "6–12 Months",
      impact: "Medium",
      effort: "Medium",
    },
    {
      title: "Formalize ESG Reporting Process",
      text: "Establish a structured reporting and data collection process.",
      timeframe: "6–12 Months",
      impact: "Medium",
      effort: "Medium",
    },
    {
      title: "Carbon Footprint Assessment",
      text: "Measure and manage carbon emissions across operations.",
      timeframe: "12+ Months",
      impact: "High",
      effort: "High",
    },
    {
      title: "Set ESG Targets",
      text: "Define measurable short, medium and long-term targets.",
      timeframe: "12+ Months",
      impact: "High",
      effort: "Medium",
    },
    {
      title: "Annual ESG Report",
      text: "Communicate performance transparently to stakeholders.",
      timeframe: "12+ Months",
      impact: "Medium",
      effort: "Medium",
    },
  ];

  const source = suggestions.length ? suggestions : fallback;

  const normalized = source.slice(0, 9).map((s, index) => ({
    title: s.title || `Recommended action ${index + 1}`,
    text: s.text || "",
    timeframe:
      s.timeframe ||
      (index < 3 ? "0–6 Months" : index < 6 ? "6–12 Months" : "12+ Months"),
    impact: s.impact || (index < 3 ? "High" : "Medium"),
    effort: s.effort || "Medium",
  }));

  while (normalized.length < 9) {
    normalized.push(fallback[normalized.length]);
  }

  return normalized;
}

function splitRoadmapGroups(items) {
  return [
    {
      label: "Priority 1",
      period: "0–6 Months",
      icon: "⏱",
      colorClass: "green",
      items: items.slice(0, 3),
    },
    {
      label: "Priority 2",
      period: "6–12 Months",
      icon: "🗓",
      colorClass: "blue",
      items: items.slice(3, 6),
    },
    {
      label: "Priority 3",
      period: "12+ Months",
      icon: "📈",
      colorClass: "purple",
      items: items.slice(6, 9),
    },
  ];
}

function whyItMattersForSuggestion(suggestion) {
  const pillar = pillarCode(suggestion.pillar);

  if (pillar === "E") {
    return "Measuring and managing environmental practices is the first step toward reducing emissions, waste and operational costs.";
  }

  if (pillar === "S") {
    return "Structured social practices help improve employee well-being, productivity, retention and long-term organisational resilience.";
  }

  if (pillar === "G") {
    return "Clear ESG governance improves accountability, consistency, internal decision-making and stakeholder trust.";
  }

  return "This action helps close a maturity gap identified in the assessment and supports a more structured ESG improvement path.";
}

function buildRecommendationItems(suggestions = []) {
  const fallback = [
    {
      title: "Define ESG Ownership",
      text: "Establish clear ESG accountability by assigning responsibilities to a dedicated individual or team.",
      pillar: "G",
      impact: "High",
      effort: "Medium",
      timeframe: "0–6 months",
    },
    {
      title: "Implement Energy Monitoring",
      text: "Track energy consumption across key facilities and processes.",
      pillar: "E",
      impact: "Medium",
      effort: "Medium",
      timeframe: "0–6 months",
    },
    {
      title: "Improve Employee Engagement",
      text: "Introduce regular engagement surveys and act on identified improvement areas.",
      pillar: "S",
      impact: "Medium",
      effort: "Medium",
      timeframe: "6–12 months",
    },
  ];

  const source = suggestions.length ? suggestions : fallback;

  const items = source.map((s, index) => ({
    title: s.title || fallback[index]?.title || `Recommendation ${index + 1}`,
    text: s.text || fallback[index]?.text || "",
    pillar: s.pillar || fallback[index]?.pillar || "",
    impact: s.impact || fallback[index]?.impact || "Medium",
    effort: s.effort || fallback[index]?.effort || "Medium",
    timeframe: s.timeframe || fallback[index]?.timeframe || "0–6 months",
  }));

  while (items.length < 3) {
    items.push(fallback[items.length]);
  }

  return items;
}

function getRecommendationIcon(pillar) {
  const p = pillarCode(pillar);
  if (p === "E") return "🌿";
  if (p === "S") return "👥";
  if (p === "G") return "🏛️";
  return "✓";
}

function getRecommendationColor(pillar) {
  const p = pillarCode(pillar);
  if (p === "E") return "var(--green)";
  if (p === "S") return "var(--blue)";
  if (p === "G") return "var(--purple)";
  return "var(--green)";
}

function buildAppendixRows(questions = [], answers = []) {
  const rows = questions.map((q, index) => {
    const answer = normalizeAnswer(answers[q.id]);

    return {
      index: index + 1,
      question: q.text || q.question || q.title || q.id,
      pillar: pillarCode(q.pillar || q.category || q.esg),
      answer: answer.label,
      maturity: answer.maturity,
      pillClass: answer.pillClass,
      score: answer.score,
    };
  });

  if (rows.length) return rows;

  return [
    {
      index: 1,
      question: "Do you have an ESG policy or sustainability strategy?",
      pillar: "G",
      answer: "Informal",
      maturity: "Informal / Ad hoc",
      pillClass: "amber",
      score: "1/4",
    },
    {
      index: 2,
      question: "Are ESG responsibilities formally assigned?",
      pillar: "G",
      answer: "No",
      maturity: "Not in place",
      pillClass: "red",
      score: "0/4",
    },
    {
      index: 3,
      question: "Do you monitor energy consumption across operations?",
      pillar: "E",
      answer: "Yes, partially",
      maturity: "Implemented & documented",
      pillClass: "mint",
      score: "3/4",
    },
  ];
}

function buildStrengthsAndImprovementsFromRows(rows = []) {
  const result = {
    Environmental: { strengths: [], improvements: [] },
    Social: { strengths: [], improvements: [] },
    Governance: { strengths: [], improvements: [] },
  };

  rows.forEach((row) => {
    const pillar = pillarLabel(row.pillar);
    const scoreText = String(row.score || "");
    const scoreValue = Number(scoreText.split("/")[0]);

    if (!result[pillar] || Number.isNaN(scoreValue)) return;

    if (scoreValue >= 3) {
      result[pillar].strengths.push(row.question);
    }

    if (scoreValue <= 1) {
      result[pillar].improvements.push(row.question);
    }
  });

  Object.keys(result).forEach((pillar) => {
    if (!result[pillar].strengths.length) {
      result[pillar].strengths.push("No major strengths identified from the submitted answers.");
    }

    if (!result[pillar].improvements.length) {
      result[pillar].improvements.push("No critical improvement areas identified from the submitted answers.");
    }

    result[pillar].strengths = result[pillar].strengths.slice(0, 3);
    result[pillar].improvements = result[pillar].improvements.slice(0, 4);
  });
  return result;
}


function listItems(items = []) {
  return items.map((item) => `<li>${escapeHtml(item)}</li>`).join("");
}

function buildHtmlReport({ assessment, options, scores, benchmark, suggestions }) {
  const sector = assessment.sector || "Unknown sector";
  const organizationName = options.organizationName || "Management Team";
  const assessmentDate =
    formatDate(assessment.completedAt || assessment.updatedAt || assessment.createdAt) ||
    formatDate(new Date());

  const footerDisclaimer =
    "EcoTrack by Viridis - ESG self-assessment export. Not a certified audit, assurance engagement or legal compliance guarantee.";

  const importantLimitation =
    "EcoTrack is a self-assessment tool. The results are indicative and depend on the accuracy of the information provided by the user. This export is not a certified ESG audit, external assurance report, legal opinion or compliance guarantee.";

  const maturity = getMaturityLabel(scores.overall);



const riskLevel = getRiskLevel(scores);
  const riskText = getRiskText(scores);
  const overallBenchmark = getOverallBenchmark(scores, benchmark);
  const weak = getWeakestPillar(scores);

  const benchmarkValues = {
    Environmental: benchmark?.Environmental ?? 60,
    Social: benchmark?.Social ?? 56,
    Governance: benchmark?.Governance ?? 60,
    Overall: overallBenchmark ?? 59,
  };

  const questions = Array.isArray(assessment.questions)
  ? assessment.questions
  : getQuestionsForSector(sector) || [];

const appendixRows = buildAppendixRows(questions, assessment.answers || {});
const keyFindings = buildKeyFindingsFromRows(appendixRows, scores);

const keyFindingsHtml = keyFindings
  .map(
    (finding) => `
      <div class="finding">
        <span class="${finding.type === "ok" ? "ok" : "warn"}">
          ${finding.type === "ok" ? "●" : "▲"}
        </span>
        ${escapeHtml(finding.text)}
      </div>
    `
  )
  .join("");
const strengths = buildStrengthsAndImprovementsFromRows(appendixRows);

const roadmapItems = buildRoadmapItems(suggestions);
const roadmapGroups = splitRoadmapGroups(roadmapItems);
const recommendationItems = buildRecommendationItems(suggestions);



  const page1Description =
    scores.overall <= 20
      ? "Your organization shows critical ESG gaps that require immediate attention, especially in the weakest ESG pillar."
      : scores.overall <= 40
      ? "Your organization has several ESG practices missing or fragmented and should prioritise core governance and data foundations."
      : scores.overall <= 60
      ? "Your organization has established several ESG practices but still shows significant opportunities for improvement, particularly in the weakest ESG areas."
      : scores.overall <= 80
      ? "Your organization shows a structured ESG approach, with opportunities to strengthen evidence, monitoring and accountability."
      : "Your organization shows a strong ESG maturity profile. The focus should now be on continuous improvement and strategic integration.";

const benchmarkInterpretation = `
  <p class="small">🌿 ${escapeHtml(
    benchmarkSentence("Environmental", scores.env, benchmarkValues.Environmental)
  )}</p>

  <p class="small">👥 ${escapeHtml(
    benchmarkSentence("Social", scores.soc, benchmarkValues.Social)
  )}</p>

  <p class="small">🏛️ ${escapeHtml(
    benchmarkSentence("Governance", scores.gov, benchmarkValues.Governance)
  )}</p>
`;

  const roadmapHtml = roadmapGroups
    .map((group, groupIndex) => {
      const actionHtml = group.items
        .map(
          (item, itemIndex) => `
          <div class="action">
            <strong>${escapeHtml(item.title)}</strong>
            ${escapeHtml(item.text)}
          </div>
        `
        )
        .join("");

      const avgImpact =
        group.items.some((i) => String(i.impact).toLowerCase().includes("high"))
          ? "High"
          : "Medium";

      const avgEffort =
        group.items.some((i) => String(i.effort).toLowerCase().includes("high"))
          ? "High"
          : "Medium";

      return `
        <div class="road">
          <div class="prio ${group.colorClass}">
            ${group.label}<br>
            <small>${group.period}</small><br>
            ${group.icon}
          </div>

          <div class="actions">${actionHtml}</div>

          <div class="metric">
            <div class="dots">${dots(avgImpact)}</div>
            ${avgImpact}
          </div>

          <div class="metric">
            <div class="dots">${dots(avgEffort)}</div>
            ${avgEffort}
          </div>
        </div>
      `;
    })
    .join("");

const recommendationPages = [];
const recommendationsPerPage = 4;

for (let i = 0; i < recommendationItems.length; i += recommendationsPerPage) {
  const pageItems = recommendationItems.slice(i, i + recommendationsPerPage);

  const pageHtml = pageItems
    .map((item) => {
      const color = getRecommendationColor(item.pillar);
      const icon = getRecommendationIcon(item.pillar);

      return `
        <div class="rec">
          <div class="icon">${icon}</div>

          <div>
            <h3>${escapeHtml(item.title)}</h3>
            <p>${escapeHtml(item.text)}</p>
            <p>
              <b style="color:${color}">Why it matters:</b>
              ${escapeHtml(whyItMattersForSuggestion(item))}
            </p>
          </div>

          <div class="recmeta">
            <b>Pillar</b> ${escapeHtml(pillarLabel(item.pillar))}<br>
            <b>Impact</b> ${escapeHtml(item.impact)} ●●●○○<br>
            <b>Effort</b> ${escapeHtml(item.effort)} ●●●○○<br>
            <b>Timeframe</b> ${escapeHtml(item.timeframe)}
          </div>
        </div>
      `;
    })
    .join("");

  recommendationPages.push(`
    <section class="page">
      <h1 class="title">
        ${i === 0 ? "Key Recommendations" : "Key Recommendations — continued"}
      </h1>
      <p class="subtitle">Detailed actions to improve your ESG performance</p>

      ${pageHtml}

      <div class="footer">
       <span>${escapeHtml(footerDisclaimer)}</span>
        <b>Recommendations</b>
      </div>
    </section>
  `);
}

const recommendationsHtml = recommendationPages.join("");

  const appendixHtml = appendixRows
    .map(
      (row) => `
        <tr>
          <td>${escapeHtml(row.index)}</td>
          <td>${escapeHtml(row.question)}</td>
          <td>${escapeHtml(row.pillar)}</td>
          <td>${escapeHtml(row.answer)}</td>
          <td><span class="pill ${row.pillClass}">${escapeHtml(row.maturity)}</span></td>
          <td>${escapeHtml(row.score)}</td>
        </tr>
      `
    )
    .join("");

  return `
<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<style>
  :root{
    --green:#148A58;
    --green2:#eaf7f0;
    --blue:#2563eb;
    --purple:#6d3fb3;
    --ink:#0f172a;
    --muted:#64748b;
    --line:#e2e8f0;
    --soft:#f8fafc;
    --yellow:#f5c542;
    --orange:#f59e0b;
  }

  *{box-sizing:border-box}

html,
body{
  width:794px;
  margin:0;
  padding:0;
  overflow:hidden;
}

*{
  box-sizing:border-box;
  min-width:0;
}

.page,
.card,
.row,
.kpis,
.findings,
.split,
.pillar,
.road,
.rec,
.actions,
.action,
.recmeta,
table,
td,
th{
  max-width:100%;
  min-width:0;
  overflow-wrap:anywhere;
  word-break:normal;
}

.page{
  background:white;
  width:794px;
  height:1123px;
  border:1px solid #dfe6ee;
  padding:28px 30px;
  position:relative;
  overflow:hidden;
  page-break-after:always;
}

.report-root{
    width:794px;
    background:#eef2f6;
}

.page{
    background:white;
    width:794px;
    height:1123px;
    border:1px solid #dfe6ee;
    padding:28px 30px;
    position:relative;
    overflow:hidden;
    page-break-after:always;
  }

  .page:before{
    content:"";
    position:absolute;
    inset:0;
    background:radial-gradient(circle at 10% 10%,rgba(20,138,88,.06),transparent 35%);
    pointer-events:none;
  }

  .header{
    display:flex;
    justify-content:space-between;
    align-items:flex-start;
    margin-bottom:22px;
    position:relative;
  }

.brand{
  font-size:28px;
  font-weight:900;
  display:flex;
  gap:10px;
  align-items:center;
}

.brand-logo{
  width:34px;
  height:34px;
  object-fit:contain;
  display:block;
}

.leaf{color:var(--green)}

  .leaf{color:var(--green)}

  .meta{
    text-align:right;
    font-size:11px;
    font-weight:700;
    line-height:1.5;
  }

  .title{
    font-size:32px;
    margin:8px 0 20px;
    font-weight:900;
    line-height:1.1;
  }

  .subtitle{
    color:var(--muted);
    font-size:15px;
    margin-top:-14px;
    margin-bottom:18px;
  }

  .card{
    border:1px solid var(--line);
    border-radius:10px;
    background:linear-gradient(135deg,#fff,#f8fffb);
    padding:18px;
  }

 .row{
  display:grid;
  grid-template-columns:230px 1fr;
  gap:16px;
}

.kpis{
  display:grid;
  grid-template-columns:repeat(4, minmax(0,1fr));
  gap:12px;
  margin:18px 0;
}

.findings{
  display:grid;
  grid-template-columns:repeat(5, minmax(0,1fr));
  gap:8px;
  margin-top:16px;
}

.split{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:16px;
}

.pillar{
  display:grid;
  grid-template-columns:145px minmax(0,1fr) minmax(0,1fr);
  gap:16px;
  border:1px solid var(--line);
  border-radius:8px;
  overflow:hidden;
  margin:14px 0;
}

.road{
  display:grid;
  grid-template-columns:120px minmax(0,1fr) 70px 70px;
  border:1px solid var(--line);
  border-radius:8px;
  overflow:hidden;
  margin:12px 0;
}

.rec{
  display:grid;
  grid-template-columns:70px minmax(0,1fr) 140px;
  gap:16px;
  border:1px solid var(--line);
  border-radius:8px;
  padding:16px;
  margin:16px 0;
}

  .small{
    font-size:12px;
    color:#111827;
    line-height:1.8;
  }

  .muted{color:var(--muted)}

  .scorebox{
    display:flex;
    align-items:center;
    gap:28px;
  }
    .scorecopy{
  flex:1;
}

.rating-badge{
  display:inline-block;
  margin-top:10px;
  padding:8px 16px;
  border-radius:999px;
  background:#ecfdf5;
  border:1px solid #bbf7d0;
  color:#065f46;
  font-size:13px;
  font-weight:900;
  width:auto;
}



.limitation-card{
  margin-top:18px;
  border:1px solid #fed7aa;
  background:#fff7ed;
  border-radius:10px;
  padding:14px 16px;
}

.limitation-card h3{
  margin:0 0 6px;
  font-size:15px;
  color:#92400e;
}

.limitation-card p{
  margin:0;
  font-size:11.5px;
  line-height:1.6;
  color:#334155;
}

  .donut{
    width:150px;
    height:150px;
    border-radius:50%;
    background:conic-gradient(var(--green) 0 ${scores.overall}%,#dcebe2 ${scores.overall}% 100%);
    display:grid;
    place-items:center;
    flex:0 0 auto;
  }

  .donut>div{
    width:104px;
    height:104px;
    background:white;
    border-radius:50%;
    display:grid;
    place-items:center;
    text-align:center;
  }

  .big{
    font-size:36px;
    font-weight:900;
    color:var(--green);
  }

  .kpis{
    display:grid;
    grid-template-columns:repeat(4,1fr);
    gap:12px;
    margin:18px 0;
  }

  .kpi{
    text-align:center;
    border:1px solid var(--line);
    border-radius:9px;
    padding:14px 10px;
  }

  .icon{font-size:28px}

  .kpi .num{
    font-size:24px;
    font-weight:900;
    margin:8px 0;
  }

  .bar{
    height:8px;
    background:#e5e7eb;
    border-radius:999px;
    overflow:hidden;
  }

  .fill{
    height:100%;
    border-radius:999px;
  }

  .green{background:var(--green)}
  .blue{background:var(--blue)}
  .purple{background:var(--purple)}
  .dark{background:#111827}

  .findings{
    display:grid;
    grid-template-columns:repeat(5,1fr);
    gap:8px;
    margin-top:16px;
  }

  .finding{
    font-size:10px;
    display:flex;
    gap:6px;
    align-items:flex-start;
  }

  .ok{
    color:var(--green);
    font-weight:900;
  }

  .warn{
    color:var(--orange);
    font-weight:900;
  }

  .footer{
    position:absolute;
    bottom:16px;
    left:30px;
    right:30px;
    border-top:1px solid var(--line);
    padding-top:9px;
    display:flex;
    justify-content:space-between;
    font-size:10px;
    color:#334155;
  }

  .footleaf{
    color:var(--green);
    font-weight:900;
  }

  .chart{
  height:250px;
  border-left:1px solid var(--line);
  border-bottom:1px solid var(--line);
  display:flex;
  align-items:flex-end;
  gap:26px;
  padding:0 26px 0 28px;
  margin:34px 0 56px;
}

  .pair{
    display:flex;
    align-items:flex-end;
    gap:8px;
    height:100%;
    position:relative;
  }

  .barv{
    width:34px;
    border-radius:3px 3px 0 0;
    position:relative;
  }

  .barv span{
    position:absolute;
    top:-22px;
    width:50px;
    left:-8px;
    text-align:center;
    font-size:12px;
    font-weight:800;
  }

  .xlabel{
    position:absolute;
    bottom:-28px;
    left:-20px;
    width:100px;
    text-align:center;
    font-size:10px;
  }

  .legend{
    font-size:11px;
    text-align:center;
    margin-bottom:-18px;
  }

  .legend i{
    display:inline-block;
    width:10px;
    height:10px;
    margin:0 5px;
  }

  .split{
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:16px;
  }

  .gauge{
    height:130px;
    margin:15px auto;
    width:190px;
    border-radius:190px 190px 0 0;
    background:conic-gradient(from 270deg,var(--yellow) 0 42%,#d1d5db 42% 50%,transparent 50%);
    position:relative;
  }



  .pillar{
    display:grid;
    grid-template-columns:145px 1fr 1fr;
    gap:16px;
    border:1px solid var(--line);
    border-radius:8px;
    overflow:hidden;
    margin:14px 0;
  }

  .pillar-left{
    background:#f0faf5;
    padding:18px;
    text-align:center;
  }

  .circle{
    width:100px;
    height:100px;
    border-radius:50%;
    margin:8px auto;
    display:grid;
    place-items:center;
    font-size:24px;
    font-weight:900;
  }

  .c-green{
    background:conic-gradient(var(--green) 0 ${scores.env}%,#dcebe2 ${scores.env}%);
  }

  .c-blue{
    background:conic-gradient(var(--blue) 0 ${scores.soc}%,#dbeafe ${scores.soc}%);
  }

  .c-purple{
    background:conic-gradient(var(--purple) 0 ${scores.gov}%,#ede9fe ${scores.gov}%);
  }

  .circle div{
    width:72px;
    height:72px;
    background:white;
    border-radius:50%;
    display:grid;
    place-items:center;
  }

  .pillar ul{
    font-size:11px;
    line-height:1.8;
    padding-left:16px;
  }

  .pillar h4{
    margin-bottom:4px;
    color:#064e3b;
  }

  .road{
    display:grid;
    grid-template-columns:120px 1fr 70px 70px;
    border:1px solid var(--line);
    border-radius:8px;
    overflow:hidden;
    margin:12px 0;
  }

  .prio{
    color:white;
    padding:20px 14px;
    font-weight:900;
    display:grid;
    place-items:center;
    text-align:center;
  }

  .actions{
    padding:10px 16px;
  }

  .action{
    margin:9px 0;
    font-size:12px;
  }

  .action strong{
    display:block;
  }

  .dots span{
    display:inline-block;
    width:8px;
    height:8px;
    background:#d1d5db;
    border-radius:50%;
    margin:2px;
  }

  .dots .on{
    background:var(--green);
  }

  .metric{
    text-align:center;
    font-size:11px;
    padding-top:22px;
  }

  .rec{
    display:grid;
    grid-template-columns:70px 1fr 140px;
    gap:16px;
    border:1px solid var(--line);
    border-radius:8px;
    padding:16px;
    margin:16px 0;
  }

  .rec h3{
    margin:0 0 8px;
    font-size:16px;
  }

  .rec p{
    font-size:12px;
    line-height:1.5;
  }

  .recmeta{
    border-left:1px solid var(--line);
    padding-left:16px;
    font-size:11px;
    line-height:2;
  }

  table{
    width:100%;
    border-collapse:collapse;
    font-size:11px;
    margin-top:20px;
  }

  th,td{
    border:1px solid var(--line);
    padding:9px;
    text-align:left;
  }

  th{
    background:#f8fafc;
    font-weight:900;
  }

  .pill{
    border-radius:999px;
    padding:4px 8px;
    font-weight:800;
    font-size:10px;
  }

  .red{
    background:#fee2e2;
    color:#b91c1c;
  }

  .amber{
    background:#fef3c7;
    color:#92400e;
  }

  .sky{
    background:#dbeafe;
    color:#1d4ed8;
  }

  .mint{
    background:#dcfce7;
    color:#166534;
  }

  .avg{
    text-align:right;
    font-size:16px;
    font-weight:900;
    color:var(--green);
    margin-top:12px;
  }
</style>
</head>

<body>
  <div class="report-root">
    <section class="page">
      <div class="header">
       <div class="brand">
  <img class="brand-logo" src="${ecoTrackLogo}" alt="EcoTrack logo" />
  <span>EcoTrack</span>
</div>
        <div class="meta">
          ESG ASSESSMENT REPORT<br>
          Assessment ID: ${escapeHtml(assessment.id || "—")}
        </div>
      </div>

      <h1 class="title">Executive Summary</h1>

      <div class="row">
        <div class="card small">
          <b>Organization</b><br>${escapeHtml(organizationName)}<br><br>
          <b>Sector</b><br>${escapeHtml(sector)}<br><br>
          <b>Assessment Date</b><br>${escapeHtml(assessmentDate)}<br><br>
          <b>Prepared for</b><br>${escapeHtml(options.userEmail || "Management Team")}
        </div>

    <div class="card scorebox">
  <div class="donut">
    <div>
      <span class="big">${scores.overall}%</span>
      <span>${escapeHtml(maturity)}</span>
    </div>
  </div>

  <div class="scorecopy">
    <p class="small">${escapeHtml(page1Description)}</p>


  </div>
</div>

      <h3>ESG Performance Overview</h3>

      <div class="kpis">
        <div class="kpi">
          <div class="icon">🌿</div>
          <b>Environmental</b>
          <div class="num" style="color:var(--green)">${scores.env}%</div>
          <div class="bar"><div class="fill green" style="width:${scores.env}%"></div></div>
        </div>

        <div class="kpi">
          <div class="icon">👥</div>
          <b>Social</b>
          <div class="num" style="color:var(--blue)">${scores.soc}%</div>
          <div class="bar"><div class="fill blue" style="width:${scores.soc}%"></div></div>
        </div>

        <div class="kpi">
          <div class="icon">🏛️</div>
          <b>Governance</b>
          <div class="num" style="color:var(--purple)">${scores.gov}%</div>
          <div class="bar"><div class="fill purple" style="width:${scores.gov}%"></div></div>
        </div>

        <div class="kpi">
          <div class="icon">◔</div>
          <b>Overall ESG</b>
          <div class="num">${scores.overall}%</div>
          <div class="bar"><div class="fill dark" style="width:${scores.overall}%"></div></div>
        </div>
      </div>

      <h3>Key Findings</h3>

 <div class="findings">
  ${keyFindingsHtml}
</div>

<div class="limitation-card">
  <h3>Important limitation</h3>
  <p>${escapeHtml(importantLimitation)}</p>
</div>

      <div class="footer">
        <span>${escapeHtml(footerDisclaimer)}</span>
        <b>Page 1</b>
      </div>
    </section>

    <section class="page">
     <div class="header">
  <div class="brand">
    <img class="brand-logo" src="${ecoTrackLogo}" alt="EcoTrack logo" />
    <span>EcoTrack</span>
  </div>
  <div></div>
</div>

      <h1 class="title">Benchmark Analysis</h1>
      <p class="subtitle">Your Performance vs Industry Average</p>

      <div class="legend">
        <i class="green"></i>Your Score
        <i style="background:#9ca3af"></i>Industry Average
      </div>

      <div class="chart">
        <div class="pair">
          <div class="barv green" style="height:${scores.env}%"><span>${scores.env}%</span></div>
          <div class="barv" style="height:${benchmarkValues.Environmental}%;background:#9ca3af"><span>${benchmarkValues.Environmental}%</span></div>
          <b class="xlabel">Environmental</b>
        </div>

        <div class="pair">
          <div class="barv green" style="height:${scores.soc}%"><span>${scores.soc}%</span></div>
          <div class="barv" style="height:${benchmarkValues.Social}%;background:#9ca3af"><span>${benchmarkValues.Social}%</span></div>
          <b class="xlabel">Social</b>
        </div>

        <div class="pair">
          <div class="barv green" style="height:${scores.gov}%"><span>${scores.gov}%</span></div>
          <div class="barv" style="height:${benchmarkValues.Governance}%;background:#9ca3af"><span>${benchmarkValues.Governance}%</span></div>
          <b class="xlabel">Governance</b>
        </div>

        <div class="pair">
          <div class="barv green" style="height:${scores.overall}%"><span>${scores.overall}%</span></div>
          <div class="barv" style="height:${benchmarkValues.Overall}%;background:#9ca3af"><span>${benchmarkValues.Overall}%</span></div>
          <b class="xlabel">Overall ESG</b>
        </div>
      </div>

      <div class="split">
        <div class="card">
          <h3>Benchmark Interpretation</h3>
          ${benchmarkInterpretation}
        </div>

        <div class="card" style="text-align:center">
          <h3>ESG Risk Level</h3>
       <div class="gauge"></div>
          <h2 style="color:var(--orange)">${escapeHtml(riskLevel)}</h2>
          <p class="small">${escapeHtml(riskText)}</p>
        </div>
      </div>

      <div class="footer">
        <span>${escapeHtml(footerDisclaimer)}</span>
        <b>Page 2</b>
      </div>
    </section>

    <section class="page">
      <h1 class="title">ESG Pillar Breakdown</h1>

      <div class="pillar">
        <div class="pillar-left">
          <div class="icon">🌿</div>
          <div class="circle c-green"><div>${scores.env}%</div></div>
        </div>
        <div>
          <h4>Strengths</h4>
          <ul>${listItems(strengths.Environmental.strengths)}</ul>
        </div>
        <div>
          <h4>Improvement Areas</h4>
          <ul>${listItems(strengths.Environmental.improvements)}</ul>
        </div>
      </div>

      <div class="pillar">
        <div class="pillar-left" style="background:#eff6ff">
          <div class="icon">👥</div>
          <div class="circle c-blue"><div>${scores.soc}%</div></div>
        </div>
        <div>
          <h4 style="color:#1d4ed8">Strengths</h4>
          <ul>${listItems(strengths.Social.strengths)}</ul>
        </div>
        <div>
          <h4 style="color:#1d4ed8">Improvement Areas</h4>
          <ul>${listItems(strengths.Social.improvements)}</ul>
        </div>
      </div>

      <div class="pillar">
        <div class="pillar-left" style="background:#f5f3ff">
          <div class="icon">🏛️</div>
          <div class="circle c-purple"><div>${scores.gov}%</div></div>
        </div>
        <div>
          <h4 style="color:#5b21b6">Strengths</h4>
          <ul>${listItems(strengths.Governance.strengths)}</ul>
        </div>
        <div>
          <h4 style="color:#5b21b6">Improvement Areas</h4>
          <ul>${listItems(strengths.Governance.improvements)}</ul>
        </div>
      </div>

      <div class="footer">
       <span>${escapeHtml(footerDisclaimer)}</span>
        <b>Page 3</b>
      </div>
    </section>

    <section class="page">
      <h1 class="title">ESG Improvement Roadmap</h1>
      <p class="subtitle">Priority Actions</p>

      ${roadmapHtml}

      <div class="footer">
       <span>${escapeHtml(footerDisclaimer)}</span>
        <b>Page 4</b>
      </div>
    </section>



      ${recommendationsHtml}

      

    <section class="page">
      <h1 class="title">Assessment Appendix</h1>
      <p class="subtitle">Detailed results by question</p>

      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Question</th>
            <th>Pillar</th>
            <th>Your Answer</th>
            <th>Maturity Level</th>
            <th>Score</th>
          </tr>
        </thead>

        <tbody>
          ${appendixHtml}
        </tbody>
      </table>

      <div class="avg">Average Score&nbsp;&nbsp; ${scores.overall}%</div>

      <div class="footer">
       <span>${escapeHtml(footerDisclaimer)}</span>
        <b>Page 6</b>
      </div>
    </section>
  </div>
</body>
</html>
  `;
}

/* ---------- PDF EXPORT ---------- */
/* ---------- PDF EXPORT ---------- */
export async function exportAssessmentPDF(assessment, options = {}) {
  if (!assessment) return;

  const sector = assessment.sector || "Unknown sector";

  const scores = getScoreFields(assessment);
  const benchmark = getBenchmark(sector, assessment);
  const suggestions = prepareSuggestions(assessment);

  const html = buildHtmlReport({
    assessment,
    options,
    scores,
    benchmark,
    suggestions,
  });

  const iframe = document.createElement("iframe");

  iframe.style.position = "fixed";
  iframe.style.left = "-9999px";
  iframe.style.top = "0";
  iframe.style.width = "794px";
  iframe.style.height = "1123px";
  iframe.style.border = "0";
  iframe.style.visibility = "hidden";

  document.body.appendChild(iframe);

  try {
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();

    await new Promise((resolve) => {
      iframe.onload = resolve;
      setTimeout(resolve, 800);
    });

    await new Promise((resolve) => setTimeout(resolve, 500));

    const pages = Array.from(iframeDoc.querySelectorAll(".page"));

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    for (let i = 0; i < pages.length; i += 1) {
      const page = pages[i];

      const canvas = await html2canvas(page, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        width: 794,
        height: 1123,
        windowWidth: 794,
        windowHeight: 1123,
        scrollX: 0,
        scrollY: 0,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.98);

      if (i > 0) pdf.addPage();

      pdf.addImage(imgData, "JPEG", 0, 0, 210, 297);
    }

    const safeSector = sanitizeFilename(sector);
    const safeDate = new Date().toISOString().slice(0, 10);
    const filePrefix = options.filePrefix || "EcoTrack_Assessment";

    pdf.save(`${filePrefix}_${safeSector}_${safeDate}.pdf`);
  } finally {
    document.body.removeChild(iframe);
  }
}